import { useContext, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { BanksArr } from '../../constants'
import type { BankNames } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useAlert } from '../../hooks'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { RowBetween, AlignCenterItem, ColumnItem } from '../../components/common'
import type { BankName } from '../../components/bankIcon/BankIcon'
import type { SecurityName } from '../../components/securityIcon/SecurityIcon'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import { SecurityIcon } from '../../components/securityIcon'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { DetailContent } from '../../components/pagingList'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { RowData } from './components'

import { Color } from '../../theme/color'

const FEE = 1000 // 수수료 1,000원

export const AccountWithdrawal = observer(() => {
  const { commonStore, myStore } = useContext(StoresContext)
  const depositStore = myStore.depositAccountStore
  const withdrawalStore = myStore.withdrawalAccountStore
  const assetSummaryStore = myStore.assetSummaryStore

  const [withdrawalBalance, setWithdrawalBalance] = useState<number>(0)

  const openAlert = useAlert()

  const possibleWithdrawal =
    (depositStore.content?.balance ?? 0) - FEE < 0 ? 0 : (depositStore.content?.balance ?? 0) - FEE
  const isOver = !withdrawalBalance ? false : possibleWithdrawal < withdrawalBalance

  useEffect(() => {
    commonStore.setShowTab(false)

    try {
      const loadData = async () => {
        await depositStore.load(undefined, true)
        await withdrawalStore.load(undefined, true)
        await assetSummaryStore.load(undefined, true)
      }
      loadData()
    } catch (error) {
      toast('페이지를 불러오는데 실패했습니다.', error)
    }
  }, [])

  const onWithdrawalBalance = async () => {
    const hour = dayjs().hour()
    const minute = dayjs().minute()

    // 23:40 ~ 00:20
    if ((hour === 23 && minute >= 40) || (hour === 0 && minute <= 20)) {
      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            은행 점검 시간이에요.
            <br />
            (23:40 ~ 00:20)
          </Text>
        ),
      })
      return
    }

    commonStore.setIsLoading(true)

    try {
      await myStore.withdrawalBalance({ originAmount: withdrawalBalance })

      commonStore.setIsLoading(false)

      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            정상 처리 되었어요.
          </Text>
        ),
      })

      await depositStore.load(undefined, true)
      await withdrawalStore.load(undefined, true)
      await assetSummaryStore.load(undefined, true)

      setWithdrawalBalance(0)
    } catch (error) {
      commonStore.setIsLoading(false)
      toast('예치금 출금에 실패했습니다.', error)
    }
  }

  const handleWithdrawalBalance = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setWithdrawalBalance(Number(value.replace(/[^0-9]/gi, '')))
  }

  return (
    <Wrap>
      <Header type="SUB" title="출금하기" />
      <StyledContent backgroundColor="gray_1">
        <DetailContent detailStore={depositStore}>
          <WithdrawalAmount>
            <Input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="금액을 입력해주세요."
              value={withdrawalBalance ? String(withdrawalBalance).replace(/[^0-9]/gi, '') : ''}
              onChange={handleWithdrawalBalance}
            />
            <ColumnItem $gap={16}>
              <RowData title="출금 가능 금액" content={`${possibleWithdrawal.toLocaleString()}원`} />
              <RowData title="수수료" content={`${FEE?.toLocaleString()}원`} />
            </ColumnItem>
            <TotalWithdrawal>
              <RowBetween>
                <Text size={18} weight={600}>
                  총 출금 금액
                </Text>
                <Text size={18} weight={600} color="primary_5">
                  {(possibleWithdrawal < 1 ? 0 : !withdrawalBalance ? 0 : withdrawalBalance + 1000).toLocaleString()}원
                </Text>
              </RowBetween>
              <Text size={10} color="text_secondary">
                (수수료 포함)
              </Text>
              {isOver && (
                <Text size={12} color="error" style={{ marginTop: '10px' }}>
                  출금 금액이 보유 예치금보다 커요.
                </Text>
              )}
            </TotalWithdrawal>
          </WithdrawalAmount>
        </DetailContent>
        <DivideLine />
        <DetailContent detailStore={withdrawalStore}>
          <WithdrawalAccount>
            <RowBetween>
              <AlignCenterItem $gap={16}>
                {BanksArr.includes(withdrawalStore?.content?.bankName as BankNames) ? (
                  <BankIcon size={46} name={withdrawalStore?.content?.bankName as BankName} />
                ) : (
                  <SecurityIcon size={46} name={withdrawalStore?.content?.bankName as SecurityName} />
                )}
                <Text size={18} weight={600}>
                  {withdrawalStore?.content?.bankName}
                </Text>
              </AlignCenterItem>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                계좌번호
              </Text>
              <Text size={14} color="text_secondary">
                {withdrawalStore?.content?.account}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                예금주
              </Text>
              <Text size={14} color="text_secondary">
                {withdrawalStore?.content?.bankOwner}
              </Text>
            </RowBetween>
          </WithdrawalAccount>
        </DetailContent>
        <NoticeContainer
          title="안내사항"
          list={[
            '신청하신 금액에서 수수료 1,000원이 제외되고 등록하신 계좌로 입금돼요.',
            '은행 전산 시스템 상태에 따라 반영되는 시간이 소요될 수 있어요.',
            '은행 점검 시간(23:40 ~ 00:20)에는 충전 및 출금 신청이 제한될 수 있어요.',
          ]}
        />
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={withdrawalBalance < 1 || isOver}
          block={true}
          onClick={onWithdrawalBalance}
        >
          출금 신청
        </Button>
      </Footer>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const WithdrawalAmount = styled(ColumnItem)`
  gap: 24px;
  padding: 56px 24px 48px;
  background-color: ${Color.white};
`

const WithdrawalAccount = styled(ColumnItem)`
  gap: 16px;
  padding: 32px 24px 40px;
  background-color: ${Color.white};
  > *:first-child {
    margin-bottom: 8px;
  }
`

const TotalWithdrawal = styled(ColumnItem)`
  align-items: flex-end;
  gap: 2px;
  > ${RowBetween} {
    width: 100%;
  }
`
